import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isTransparent = isHome && !scrolled;

  const linkClass = `text-sm whitespace-nowrap transition-colors duration-300 ${
    isTransparent
      ? "text-white/90 hover:text-white font-medium"
      : "text-foreground-700 hover:text-foreground-950 font-medium"
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`text-lg font-bold whitespace-nowrap transition-colors duration-300 ${
              isTransparent ? "text-white" : "text-foreground-950"
            }`}
          >
            CSSBuy Spreadsheet
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={linkClass}>
              Início
            </Link>

            <Link to="/artigo/cssbuy-spreadsheet-guia-completo" className={linkClass}>
              Guia de Compras
            </Link>

            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`flex items-center gap-1 text-sm whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                  isTransparent
                    ? "text-white/90 hover:text-white font-medium"
                    : "text-foreground-700 hover:text-foreground-950 font-medium"
                }`}
              >
                Categorias
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${categoryOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {categoryOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl border border-background-200/60 overflow-hidden z-50 w-56 max-h-[360px] overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setCategoryOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {isHome ? (
              <a href="#artigos" className={linkClass}>
                Artigos SEO
              </a>
            ) : (
              <Link to="/" className={linkClass}>
                Artigos SEO
              </Link>
            )}

            <Link to="/artigo/cssbuy-spreadsheet-guia-completo" className={linkClass}>
              Blog
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm bg-primary-500 text-background-50 px-5 py-2 rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer font-medium"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors duration-300 cursor-pointer ${
              isTransparent ? "text-white" : "text-foreground-950"
            }`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-4 flex flex-col gap-2">
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-foreground-700 hover:text-foreground-950 transition-colors py-2.5 px-2 rounded-lg hover:bg-background-100"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              Início
            </Link>

            <Link
              to="/artigo/cssbuy-spreadsheet-guia-completo"
              className="flex items-center gap-2 text-sm text-foreground-700 hover:text-foreground-950 transition-colors py-2.5 px-2 rounded-lg hover:bg-background-100"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              Guia de Compras
            </Link>

            <div className="border-t border-background-200/60 pt-2 mt-1">
              <p className="text-xs text-foreground-500 px-2 mb-2 uppercase tracking-wider">Categorias</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 text-sm text-foreground-700 hover:text-foreground-950 transition-colors py-2 px-2 rounded-lg hover:bg-background-100 cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
                      <i className={cat.icon}></i>
                    </span>
                    <span className="truncate text-xs">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-2 mt-1">
              {isHome ? (
                <a
                  href="#artigos"
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-foreground-950 transition-colors py-2.5 px-2 rounded-lg hover:bg-background-100"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-article-line"></i>
                  </span>
                  Artigos SEO
                </a>
              ) : (
                <Link
                  to="/"
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-foreground-950 transition-colors py-2.5 px-2 rounded-lg hover:bg-background-100"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-article-line"></i>
                  </span>
                  Artigos SEO
                </Link>
              )}
              <Link
                to="/artigo/cssbuy-spreadsheet-guia-completo"
                className="flex items-center gap-2 text-sm text-foreground-700 hover:text-foreground-950 transition-colors py-2.5 px-2 rounded-lg hover:bg-background-100"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-newspaper-line"></i>
                </span>
                Blog
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 text-sm bg-primary-500 text-background-50 px-4 py-2.5 rounded-lg hover:bg-primary-600 transition-colors duration-200 cursor-pointer mt-2 text-center justify-center font-medium"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-line"></i>
                </span>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}